import {
	CarrierSearch, ImpersonatedCarrier, ImpersonatedShipper, ShipperSearch, TJWT
} from '@types';
import ApiClient from '@api/axiosSetup';
import { jwtDecode } from 'jwt-decode';

export const IMPERSONATION_LOCAL_STORAGE_KEY = {
	carrier: 'a1f65eb1-2717-4edf-97fd-5a07ddb27ced2',
	shipper: 'ac63ba8e-2d80-4145-8b98-73c5baa7dbbb2',
};

// Only admins, and shippers linked to more than one customer, may impersonate.
export const canImpersonateFromJwt = (jwt: string | null): boolean => {
	if (jwt) {
		const decoded: TJWT = jwtDecode(jwt);
		if (decoded.user_type === 'admin') {
			return true;
		}

		if (decoded.user_type === 'shipper'
			&& decoded.customer_ids && decoded.customer_ids.length > 1
		) {
			return true;
		}
	}

	return false;
}

type ActiveShipperCarrierTokenResp = {
	id: number;
	customer_id: string;
	token: number;
	business_name: string;
	is_active?: boolean;
	deactivation_reason?: string;
	awaiting_tnc?: boolean;
	is_marketplace_enabled?: boolean;
}

export const getIdOrExternalId = (id: null|string): number|string|null => {
	if (id === null) {
		return null;
	}

	const shipperId = parseInt(id, 10);
	if (!isNaN(shipperId) && shipperId >= 0) {
		return shipperId;
	}

	if (id.startsWith('s') || id.startsWith('c')) {
		return id;
	}
	return null;
}

export const fetchShipperById = async (id: number|string): Promise<ImpersonatedShipper> => {
	try {
		const response = await ApiClient.post(`/api/shipper/get-active-shipper-token?shipperId=${id}`);
		const fetchedShipper = response.data.data as ActiveShipperCarrierTokenResp;
		return {
			id: fetchedShipper.id,
			customer_id: fetchedShipper.customer_id,
			name: fetchedShipper.business_name,
			label: fetchedShipper.business_name,
			token: fetchedShipper.token.toString(),
			type: 'shipper',
			isActive: fetchedShipper.is_active,
			deactivationReason: fetchedShipper.deactivation_reason,
			awaitingTNC: fetchedShipper.awaiting_tnc,
		};
	} catch (error) {
		console.error(`Failed to fetch shipper with ID ${id}:`, error);
		throw error;
	}
};

export const fetchCarrierById = async (id: number|string): Promise<ImpersonatedCarrier> => {
	try {
		const response = await ApiClient.post(`/api/carrier/get-active-carrier-token?carrierId=${id}`);
		const fetchedCarrier = response.data.data as ActiveShipperCarrierTokenResp;
		return {
			id: fetchedCarrier.id,
			customer_id: fetchedCarrier.customer_id,
			name: fetchedCarrier.business_name,
			label: fetchedCarrier.business_name,
			token: fetchedCarrier.token.toString(),
			isActive: fetchedCarrier.is_active,
			type: 'carrier',
			deactivationReason: fetchedCarrier.deactivation_reason,
			is_marketplace_enabled: fetchedCarrier.is_marketplace_enabled
		};
	} catch (error) {
		console.error(`Failed to fetch carrier with ID ${id}:`, error);
		throw error;
	}
};

export const buildImpersonatedShipper = (impersonation: ShipperSearch): ImpersonatedShipper => ({
	id: impersonation.shipper_id,
	customer_id: impersonation.customer_external_id!,
	external_id: impersonation.customer_external_id!,
	name: impersonation.trade_name!,
	label: impersonation.trade_name!,
	token: impersonation.shipper_id!.toString(),
	type: 'shipper',
	isActive: impersonation.is_active,
	deactivationReason: impersonation.deactivation_reason,
	awaitingTNC: impersonation.awaiting_tnc,
});

export const buildImpersonatedCarrier = (impersonation: CarrierSearch): ImpersonatedCarrier => ({
	id: impersonation.carrier_id!,
	customer_id: impersonation.customer_external_id!,
	external_id: impersonation.customer_external_id!,
	name: impersonation.trade_name!,
	label: impersonation.trade_name!,
	token: impersonation.carrier_id!.toString(),
	isActive: impersonation.is_active,
	type: 'carrier',
	deactivationReason: impersonation.deactivation_reason,
	is_marketplace_enabled: impersonation.is_marketplace_enabled ?? false
});
